export const TOWER_PROGRESS_VERSION = 1 as const;

export type TowerProgress = {
  version: typeof TOWER_PROGRESS_VERSION;
  /** Highest floor cleared (1–100), 0 = none. */
  highestCleared: number;
  /** Best consecutive battle kills (tower/raid/dungeon combat only). */
  bestKillStreak: number;
};

const STORAGE_PREFIX = "mind-survival/tower/v1";

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function createInitialTowerProgress(): TowerProgress {
  return {
    version: TOWER_PROGRESS_VERSION,
    highestCleared: 0,
    bestKillStreak: 0,
  };
}

export function normalizeTowerProgress(raw: unknown): TowerProgress {
  const base = createInitialTowerProgress();
  if (!raw || typeof raw !== "object") return base;
  const p = raw as Partial<TowerProgress>;
  const highest =
    typeof p.highestCleared === "number" && Number.isFinite(p.highestCleared)
      ? Math.min(100, Math.max(0, Math.floor(p.highestCleared)))
      : 0;
  const bestKillStreak =
    typeof p.bestKillStreak === "number" && Number.isFinite(p.bestKillStreak)
      ? Math.max(0, Math.floor(p.bestKillStreak))
      : 0;
  return {
    version: TOWER_PROGRESS_VERSION,
    highestCleared: highest,
    bestKillStreak,
  };
}

export function loadTowerProgress(userId: string): TowerProgress {
  const raw = localStorage.getItem(storageKey(userId));
  if (!raw) return createInitialTowerProgress();
  try {
    return normalizeTowerProgress(JSON.parse(raw));
  } catch {
    localStorage.removeItem(storageKey(userId));
    return createInitialTowerProgress();
  }
}

export function saveTowerProgress(userId: string, progress: TowerProgress): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(progress));
  import("@/cloud/player-cloud-scheduler").then((m) =>
    m.schedulePlayerCloudPush(userId, "gameState"),
  );
}

export function maxSelectableFloor(progress: TowerProgress): number {
  return Math.min(100, progress.highestCleared + 1);
}
